import React from "react";
import useFrontPageCta from "../../../Hooks/useFrontPageCta";
import { Link } from "react-router-dom";

// Assets
import IconBrowser from "../../../Assets/SVGs/Icons/Devices/Browser";
import MobilePhone from "../../../Assets/SVGs/Icons/Devices/MobilePhone";
import IconTV from "../../../Assets/SVGs/Icons/Devices/TV";

// Constants
import { RoutePaths } from "../../../Constants/RoutePaths";

const APPLE_APP_STORE_URL =
    "https://apps.apple.com/us/app/ickonic/id1551307977";
const GOOGLE_PLAY_URL =
    "https://play.google.com/store/apps/details?id=com.ickonic.ickonicandroid&pli=1";
const ROKU_CHANNEL_URL =
    "https://channelstore.roku.com/en-gb/details/5011ff0342945759843caac15829bf8b:43438db7b0c67f954fb786200a46fc64/ickonic";
const FIRE_TV_URL = "https://www.amazon.com/gp/product/B0H476SKRG";

// Store scores are reviewed static content: the stores do not provide one
// uniform, official, client-safe ratings API.

interface PlatformRatingProps {
    name: string;
    rating: number;
}

const PlatformRating: React.FC<PlatformRatingProps> = ({ name, rating }) => {
    const boundedRating = Math.max(0, Math.min(rating, 5));
    const formattedRating = boundedRating.toFixed(1);
    const fillPercentage = `${(boundedRating / 5) * 100}%`;

    return (
        <span
            className="front-devices__platform-rating"
            role="img"
            aria-label={`${name} rating: ${formattedRating} out of 5`}
        >
            <span className="front-devices__rating-score" aria-hidden="true">
                {formattedRating} / 5
            </span>
            <span
                className="front-devices__rating-stars"
                style={
                    {
                        "--front-devices-rating-fill": fillPercentage,
                    } as React.CSSProperties
                }
                aria-hidden="true"
            >
                <span className="front-devices__rating-stars-base">
                    {"\u2605\u2605\u2605\u2605\u2605"}
                </span>
                <span className="front-devices__rating-stars-fill">
                    {"\u2605\u2605\u2605\u2605\u2605"}
                </span>
            </span>
        </span>
    );
};

interface ExternalPlatformLinkProps {
    href: string;
    mark: string;
    name: string;
    action: string;
    markClassName?: string;
    rating?: number;
}

const ExternalPlatformLink: React.FC<ExternalPlatformLinkProps> = ({
    href,
    mark,
    name,
    action,
    markClassName = "",
    rating,
}) => (
    <a
        className="front-devices__platform-card"
        href={href}
        target="_blank"
        rel="noopener noreferrer"
    >
        <span
            className={`front-devices__platform-mark ${markClassName}`.trim()}
            aria-hidden="true"
        >
            {mark}
        </span>
        <span className="front-devices__platform-copy">
            <strong>{name}</strong>
            <span className="front-devices__platform-action">{action}</span>
            {rating !== undefined ? (
                <PlatformRating name={name} rating={rating} />
            ) : null}
        </span>
        <span className="front-devices__platform-arrow" aria-hidden="true">
            {"\u2197"}
        </span>
    </a>
);

const FrontDevicesSection: React.FC = () => {
    const cta = useFrontPageCta("Start watching", RoutePaths.Browse);

    return <section
        id="apps"
        className="front-devices"
        aria-labelledby="front-devices-heading"
        tabIndex={-1}
    >
        <div className="front-devices__inner">
            <div className="front-devices__showcase">
                <div className="front-devices__copy">
                    <p className="front-devices__kicker">Watch anywhere</p>
                    <h2 id="front-devices-heading">
                        Made for the screen in front of you.
                    </h2>
                    <p className="front-devices__summary">
                        Take independent films, current affairs and original
                        series with you, from your browser and mobile to the
                        biggest screen in the room.
                    </p>

                    <ul className="front-devices__benefits">
                        <li>
                            <span aria-hidden="true">
                                <IconBrowser iconFill="currentColor" />
                            </span>
                            Watch instantly in your web browser
                        </li>
                        <li>
                            <span aria-hidden="true">
                                <MobilePhone iconFill="currentColor" />
                            </span>
                            Take your library with you on iOS and Android
                        </li>
                        <li>
                            <span aria-hidden="true">
                                <IconTV iconFill="currentColor" />
                            </span>
                            Settle in with supported TV and casting apps
                        </li>
                    </ul>
                </div>

                <ul className="front-devices__platform-grid">
                    <li>
                        <Link
                            className="front-devices__platform-card"
                            to={cta.link}
                        >
                            <span
                                className="front-devices__platform-mark front-devices__platform-mark--web"
                                aria-hidden="true"
                            >
                                WEB
                            </span>
                            <span className="front-devices__platform-copy">
                                <strong>Web browser</strong>
                                <span className="front-devices__platform-action">
                                    {cta.text}
                                </span>
                            </span>
                            <span
                                className="front-devices__platform-arrow"
                                aria-hidden="true"
                            >
                                {"\u2192"}
                            </span>
                        </Link>
                    </li>
                    <li>
                        <ExternalPlatformLink
                            href={APPLE_APP_STORE_URL}
                            mark="APP"
                            markClassName="front-devices__platform-mark--apple"
                            name="Apple App Store"
                            action="Download for iPhone and iPad"
                            rating={4.8}
                        />
                    </li>
                    <li>
                        <ExternalPlatformLink
                            href={GOOGLE_PLAY_URL}
                            mark="PLAY"
                            markClassName="front-devices__platform-mark--google"
                            name="Google Play"
                            action="Get the Android app"
                            rating={4.8}
                        />
                    </li>
                    <li>
                        <ExternalPlatformLink
                            href={ROKU_CHANNEL_URL}
                            mark="ROKU"
                            markClassName="front-devices__platform-mark--roku"
                            name="Roku"
                            action="Add the Ickonic channel"
                            rating={5}
                        />
                    </li>
                    <li>
                        <ExternalPlatformLink
                            href={FIRE_TV_URL}
                            mark="fire tv"
                            markClassName="front-devices__platform-mark--fire"
                            name="Fire TV"
                            action="Get the Fire TV app"
                        />
                    </li>
                    <li>
                        <div className="front-devices__platform-card front-devices__platform-card--static">
                            <span
                                className="front-devices__platform-mark front-devices__platform-mark--cast"
                                aria-hidden="true"
                            >
                                CAST
                            </span>
                            <span className="front-devices__platform-copy">
                                <strong>Chromecast</strong>
                                <span className="front-devices__platform-action">
                                    Cast from a supported device
                                </span>
                            </span>
                        </div>
                    </li>
                </ul>
            </div>
        </div>
    </section>;
};

export default FrontDevicesSection;
